// DatosMaquinaLider — contenedor de lo que la máquina deja pendiente para el
// líder en su línea: paros sin causal (banner) y horas por completar (card).
//
// En líneas manuales ambas consultas vuelven vacías y no se renderiza nada:
// la captura L2 queda idéntica. Poll de 30 s como los banners hermanos.
//
// "Ver como": se lee la suplantación desde sessionStorage (`verComoTargetId`,
// la misma fuente que adjunta el header X-Ver-Como) y se ocultan las acciones;
// el API respondería 403 a cualquier escritura.

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { CausalParoTreeNode, ParoPendienteCausal } from 'schemas';
import { ApiError } from '../../lib/api/client';
import {
  useCompletarCausalParo,
  useCompletarRegistro,
  useParosPendientesCausal,
  useRegistrosPendientesCompletar,
} from '../../lib/api/ingesta';
import { verComoTargetId } from '../../lib/ver-como';
import { BannerParosPendientesCausal, CompletarCausalDialog } from './BannerParosPendientesCausal';
import { PendientesCompletarCard } from './PendientesCompletarCard';

function mensajeError(err: unknown, fallback: string): string {
  return err instanceof ApiError || err instanceof Error ? err.message : fallback;
}

export function DatosMaquinaLider({
  lineaId,
  causales,
  fuenteCaptura,
}: {
  lineaId: string;
  causales: CausalParoTreeNode[];
  /** Fuente de la línea (contexto-lider): híbrida/máquina → poll de 10 s (R12). */
  fuenteCaptura?: string | null;
}) {
  const soloLectura = verComoTargetId() !== null;
  const parosQ = useParosPendientesCausal({ fuenteCaptura });
  const registrosQ = useRegistrosPendientesCompletar({ fuenteCaptura });
  const completarCausal = useCompletarCausalParo();
  const completarRegistro = useCompletarRegistro();

  const paros = useMemo(
    () => (parosQ.data ?? []).filter((p) => p.linea_id === lineaId),
    [parosQ.data, lineaId],
  );
  const registros = useMemo(
    () => (registrosQ.data ?? []).filter((r) => r.linea_id === lineaId),
    [registrosQ.data, lineaId],
  );

  const [paroACompletar, setParoACompletar] = useState<ParoPendienteCausal | null>(null);
  const [errorCausal, setErrorCausal] = useState<string | null>(null);
  const [guardandoId, setGuardandoId] = useState<string | null>(null);

  if (paros.length === 0 && registros.length === 0) return null;

  return (
    <>
      <BannerParosPendientesCausal
        paros={paros}
        soloLectura={soloLectura}
        onCompletar={(p) => {
          setErrorCausal(null);
          setParoACompletar(p);
        }}
      />
      <PendientesCompletarCard
        registros={registros}
        soloLectura={soloLectura}
        guardandoId={guardandoId}
        onConfirmar={(registroId, body) => {
          setGuardandoId(registroId);
          completarRegistro.mutate(
            { registroId, body },
            {
              onSuccess: () => toast.success('Hora completada'),
              onError: (err) => toast.error(mensajeError(err, 'No se pudo completar la hora')),
              onSettled: () => setGuardandoId(null),
            },
          );
        }}
      />
      {paroACompletar ? (
        <CompletarCausalDialog
          paro={paroACompletar}
          causales={causales}
          guardando={completarCausal.isPending}
          serverError={errorCausal}
          onClose={() => setParoACompletar(null)}
          onGuardar={(body) => {
            setErrorCausal(null);
            completarCausal.mutate(
              { paroId: paroACompletar.id, body },
              {
                onSuccess: () => {
                  toast.success('Causal asignada');
                  setParoACompletar(null);
                },
                onError: (err) => setErrorCausal(mensajeError(err, 'No se pudo asignar la causal')),
              },
            );
          }}
        />
      ) : null}
    </>
  );
}
